import { Card, Form, Button, Row, Col, Alert, Badge, Spinner } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageFade from '../../components/animation/PageFade';
import {
  useGetMetadataQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
} from '../../services/api';
import { toast } from 'react-toastify';
import { ProductVariant } from '../../types';
import { FaTimes, FaUpload } from 'react-icons/fa';

// Helper to extract IDs from potentially nested or populated data
const ensureId = (item: any): string => {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') return item._id || item.id || '';
  return '';
};

const extractIds = (data: any): string[] => {
  if (Array.isArray(data)) {
    return data.flatMap(extractIds);
  }
  const id = ensureId(data);
  return id ? [id] : [];
};

export default function ProductForm() {
  const { id } = useParams<{ id?: string }>();
  // ProductForm is only used for /products/new and /products/:id/edit
  // So if id exists, we're in edit mode
  const isEdit = !!id;
  const navigate = useNavigate();
  const { data: metadata, isLoading: metadataLoading } = useGetMetadataQuery();
  const { data: productData, isLoading: productLoading } = useGetProductQuery(id || '', { skip: !id });
  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    priceBeforeDiscount: '',
    sku: '',
    inStock: true,
    isVisible: true,
    material: '',
    careInstructions: '',
    countryOfOrigin: '',
    categories: [] as string[],
    colors: [] as string[],
    sizes: [] as string[],
  });

  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [images, setImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && productData?.data) {
      // The API returns { success: true, data: Product }
      // So the actual product is in productData.data.data
      const product = (productData.data as any).data || productData.data;
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        priceBeforeDiscount: product.priceBeforeDiscount?.toString() || '',
        sku: product.sku || '',
        inStock: product.inStock ?? true,
        isVisible: product.isVisible ?? true,
        material: product.material || '',
        careInstructions: product.careInstructions || '',
        countryOfOrigin: product.countryOfOrigin || '',
        // Robust ID extraction for populated fields
        categories: extractIds(product.categories),
        colors: extractIds(product.colors),
        sizes: extractIds(product.sizes),
      });

      // DEBUG: Log the product data to help identify the structure of variants
      console.log('DEBUG [ProductForm]: Loading product data for edit:', product);

      // In some cases variants might be returned as a JSON string, or as a singular 'variant' property
      let rawVariants = product.variants || (product as any).variant || [];
      console.log('DEBUG [ProductForm]: Raw variants found:', rawVariants);

      if (typeof rawVariants === 'string') {
        try {
          rawVariants = JSON.parse(rawVariants);
          console.log('DEBUG [ProductForm]: Parsed variants from string:', rawVariants);
        } catch (error) {
          console.error('Failed to parse variants JSON string:', error);
          rawVariants = [];
        }
      }

      // Final defensive check to ensure rawVariants is an array
      rawVariants = Array.isArray(rawVariants) ? rawVariants : [];

      // Ensure variants only store IDs for size and color
      const sanitizedVariants = rawVariants.map((v: any, idx: number) => {
        const s = ensureId(v.size);
        const c = ensureId(v.color);
        console.log(`DEBUG [ProductForm]: Variant ${idx} sanitized: size=${s}, color=${c}`);
        return {
          ...v,
          size: s,
          color: c,
        };
      });
      console.log('DEBUG [ProductForm]: Final sanitized variants state:', sanitizedVariants);
      setVariants(sanitizedVariants);
      setExistingImages(product.images || []);
    }
  }, [isEdit, productData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'categories' || name === 'colors' || name === 'sizes') {
      const select = e.target as HTMLSelectElement;
      const selectedValues = Array.from(select.selectedOptions, (option) => option.value);
      setFormData((prev) => ({ ...prev, [name]: selectedValues }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    setImages((prev) => [...prev, ...files]);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index: number) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const addVariant = () => {
    setVariants((prev) => [...prev, { size: '', color: '', stock: 0 }]);
  };

  const removeVariant = (index: number) => {
    setVariants((prev) => prev.filter((_, i) => i !== index));
  };

  const updateVariant = (index: number, field: keyof ProductVariant, value: string | number) => {
    setVariants((prev) => prev.map((v, i) => (i === index ? { ...v, [field]: value } : v)));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }
    if (variants.length > 0) {
      variants.forEach((variant, index) => {
        if (!variant.size) {
          newErrors[`variant_${index}_size`] = 'Size is required';
        }
        if (!variant.color) {
          newErrors[`variant_${index}_color`] = 'Color is required';
        }
        if (variant.stock < 0) {
          newErrors[`variant_${index}_stock`] = 'Stock must be 0 or greater';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    const submitFormData = new FormData();
    submitFormData.append('name', formData.name);
    if (formData.description) submitFormData.append('description', formData.description);
    submitFormData.append('price', formData.price);
    if (formData.priceBeforeDiscount) submitFormData.append('priceBeforeDiscount', formData.priceBeforeDiscount);
    if (formData.sku) submitFormData.append('sku', formData.sku);
    submitFormData.append('inStock', formData.inStock.toString());
    submitFormData.append('isVisible', formData.isVisible.toString());
    if (formData.material) submitFormData.append('material', formData.material);
    if (formData.careInstructions) submitFormData.append('careInstructions', formData.careInstructions);
    if (formData.countryOfOrigin) submitFormData.append('countryOfOrigin', formData.countryOfOrigin);

    // Append arrays - API expects individual entries for each ID
    const categoryIds = extractIds(formData.categories);
    categoryIds.forEach((catId) => {
      submitFormData.append('categories', catId);
    });

    const colorIds = extractIds(formData.colors);
    colorIds.forEach((colorId) => {
      submitFormData.append('colors', colorId);
    });

    const sizeIds = extractIds(formData.sizes);
    sizeIds.forEach((sizeId) => {
      submitFormData.append('sizes', sizeId);
    });

    // Append variants as JSON string, ensuring they only contain IDs
    if (variants.length > 0) {
      const sanitizedVariants = variants.map(v => ({
        size: ensureId(v.size),
        color: ensureId(v.color),
        stock: v.stock || 0
      }));
      submitFormData.append('variants', JSON.stringify(sanitizedVariants));
    }

    // Append NEW images (files)
    images.forEach((image) => {
      submitFormData.append('images', image);
    });

    // For updates, send existing images that should be kept
    if (isEdit && existingImages.length > 0) {
      submitFormData.append('existingImages', JSON.stringify(existingImages));
    }

    // Debug: Log FormData contents (for development)
    if (import.meta.env.DEV) {
      console.log('FormData contents:');
      for (const [key, value] of submitFormData.entries()) {
        if (value instanceof File) {
          console.log(`${key}:`, value.name, `(${value.size} bytes, ${value.type})`);
        } else {
          console.log(`${key}:`, value);
        }
      }
    }

    try {
      if (isEdit && id) {
        await updateProduct({ id, formData: submitFormData }).unwrap();
        toast.success('Product updated successfully');
      } else {
        await createProduct(submitFormData).unwrap();
        toast.success('Product created successfully');
      }
      navigate('/products');
    } catch (error: any) {
      toast.error(error?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} product`);
    }
  };

  if (metadataLoading || (isEdit && productLoading)) {
    return (
      <PageFade>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </PageFade>
    );
  }

  const categories = metadata?.data?.categories || (metadata as any)?.categories || [];
  const colors = metadata?.data?.colors || (metadata as any)?.colors || [];
  const sizes = metadata?.data?.sizes || (metadata as any)?.sizes || [];

  return (
    <PageFade>
      <Card className="shadow-sm">
        <Card.Body>
          <Card.Title>{isEdit ? 'Edit Product' : 'New Product'}</Card.Title>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Product name"
                    required
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>SKU</Form.Label>
                  <Form.Control
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    placeholder="Auto-generated if omitted"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Price (EGP) *</Form.Label>
                  <Form.Control
                    name="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!errors.price}
                  />
                  <Form.Control.Feedback type="invalid">{errors.price}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Price Before Discount (EGP)</Form.Label>
                  <Form.Control
                    name="priceBeforeDiscount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.priceBeforeDiscount}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Country of Origin</Form.Label>
                  <Form.Control
                    name="countryOfOrigin"
                    value={formData.countryOfOrigin}
                    onChange={handleInputChange}
                    placeholder="e.g., Egypt"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Material</Form.Label>
                  <Form.Control
                    name="material"
                    value={formData.material}
                    onChange={handleInputChange}
                    placeholder="e.g., 100% COTTON"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Product description"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Care Instructions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="careInstructions"
                    value={formData.careInstructions}
                    onChange={handleInputChange}
                    placeholder="Care instructions"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Categories</Form.Label>
                  <Form.Select
                    name="categories"
                    multiple
                    value={formData.categories}
                    onChange={handleInputChange}
                    htmlSize={Math.min(categories.length + 1, 5)}
                  >
                    {categories.map((cat: any) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Hold Ctrl/Cmd to select multiple</Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Colors</Form.Label>
                  <Form.Select
                    name="colors"
                    multiple
                    value={formData.colors}
                    onChange={handleInputChange}
                    htmlSize={Math.min(colors.length + 1, 5)}
                  >
                    {colors.map((color: any) => (
                      <option key={color._id} value={color._id}>
                        {color.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Hold Ctrl/Cmd to select multiple</Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Sizes</Form.Label>
                  <Form.Select
                    name="sizes"
                    multiple
                    value={formData.sizes}
                    onChange={handleInputChange}
                    htmlSize={Math.min(sizes.length + 1, 5)}
                  >
                    {sizes.map((size: any) => (
                      <option key={size._id} value={size._id}>
                        {size.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Hold Ctrl/Cmd to select multiple</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="inStock"
                  name="inStock"
                  label="In Stock"
                  checked={formData.inStock}
                  onChange={handleInputChange}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="isVisible"
                  name="isVisible"
                  label="Visible to Customers"
                  checked={formData.isVisible}
                  onChange={handleInputChange}
                />
              </Col>
              <Col md={12}>
                <hr />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Variants</h5>
                  <Button type="button" variant="outline-primary" size="sm" onClick={addVariant}>
                    Add Variant
                  </Button>
                </div>
                {variants.map((variant, index) => (
                  <Card key={index} className="mb-3">
                    <Card.Body>
                      <Row className="g-2">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Size</Form.Label>
                            <Form.Select
                              value={variant.size}
                              onChange={(e) => updateVariant(index, 'size', e.target.value)}
                              isInvalid={!!errors[`variant_${index}_size`]}
                            >
                              <option value="">Select size</option>
                              {sizes.map((size: any) => (
                                <option key={size._id} value={size._id}>
                                  {size.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Color</Form.Label>
                            <Form.Select
                              value={variant.color}
                              onChange={(e) => updateVariant(index, 'color', e.target.value)}
                              isInvalid={!!errors[`variant_${index}_color`]}
                            >
                              <option value="">Select color</option>
                              {colors.map((color: any) => (
                                <option key={color._id} value={color._id}>
                                  {color.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Stock</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              value={variant.stock}
                              onChange={(e) => updateVariant(index, 'stock', parseInt(e.target.value) || 0)}
                              isInvalid={!!errors[`variant_${index}_stock`]}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={1} className="d-flex align-items-end">
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            onClick={() => removeVariant(index)}
                          >
                            <FaTimes />
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
                {variants.length === 0 && (
                  <Alert variant="info">Add variants to manage stock by size and color combination.</Alert>
                )}
              </Col>
              <Col md={12}>
                <hr />
                <Form.Group>
                  <Form.Label>Product Images (Max 5)</Form.Label>
                  {existingImages.length > 0 && (
                    <div className="mb-3 d-flex flex-wrap gap-2">
                      {existingImages.map((img, index) => (
                        <div key={index} className="position-relative d-inline-block">
                          <img
                            src={img}
                            alt={`Existing ${index + 1}`}
                            style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0"
                            style={{ transform: 'translate(50%, -50%)' }}
                            onClick={() => removeExistingImage(index)}
                          >
                            <FaTimes />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {images.length > 0 && (
                    <div className="mb-3 d-flex flex-wrap gap-2">
                      {images.map((img, index) => (
                        <div key={index} className="position-relative d-inline-block">
                          <img
                            src={URL.createObjectURL(img)}
                            alt={`New ${index + 1}`}
                            style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0"
                            style={{ transform: 'translate(50%, -50%)' }}
                            onClick={() => removeImage(index)}
                          >
                            <FaTimes />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {images.length + existingImages.length < 5 && (
                    <Form.Control
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      disabled={images.length + existingImages.length >= 5}
                    />
                  )}
                  <Form.Text className="text-muted">
                    {images.length + existingImages.length} / 5 images selected
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12} className="text-end">
                <Button variant="secondary" className="me-2" onClick={() => navigate('/products')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreating || isUpdating}>
                  {isCreating || isUpdating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {isEdit ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    isEdit ? 'Update Product' : 'Create Product'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </PageFade>
  );
}
